import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getHolidaySettings, saveHolidaySettings } from 'apis/publicHolidayAPIs';
import { showErrorSnackbar, showSuccessSnackbar } from 'store/actions/snackbarMessage';
import { checkMenuAdmin } from 'util/userUtils';

// General Settings → Public Holidays. Admin and menuAdmin only (the server enforces it): maintain public holidays per state and year. Merchants'
// "Load Public Holidays" button uses these before the cached government API data, so this is the
// only source for states without an API (SA).

const STATES = ['SA', 'VIC', 'NSW', 'QLD', 'WA', 'TAS', 'NT', 'ACT'];
const DEFAULT_STATE = 'SA';
const CURRENT_YEAR = moment().year();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

const SOURCE_LABELS = {
  manual: 'Maintained here',
  cache: 'Cached from the government API (saving makes this list the maintained one)',
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  select: {
    minWidth: 120,
  },
  section: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

let nextRowKey = 0;
const toRow = (holiday) => ({
  key: nextRowKey++,
  date: holiday.date || '',
  name: holiday.name || '',
  description: holiday.description || '',
});

const PublicHolidaySettings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((reduxState) => reduxState.account.user);
  const canEdit = checkMenuAdmin(user);
  const [state, setState] = useState(DEFAULT_STATE);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = (error, fallbackMessage) => {
    console.error(fallbackMessage, error);
    if (error.response && error.response.status === 403) {
      history.push('/sign-in');
      return;
    }
    const message = error.response && error.response.data && error.response.data.error;
    dispatch(showErrorSnackbar(message || fallbackMessage));
  };

  const applySettings = (data) => {
    setSettings(data);
    setRows((data.data || []).map(toRow));
  };

  const load = async () => {
    setIsLoading(true);
    try {
      applySettings(await getHolidaySettings(state, year));
    } catch (error) {
      setSettings(null);
      setRows([]);
      handleError(error, 'Cannot load public holidays');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!canEdit) {
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, year, canEdit]);

  const updateRow = (key, field, value) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const addRow = () => {
    setRows((current) => [...current, toRow({})]);
  };

  const removeRow = (key) => {
    setRows((current) => current.filter((row) => row.key !== key));
  };

  const save = async () => {
    setIsLoading(true);
    try {
      const holidays = rows.map(({ date, name, description }) => ({ date, name, description }));
      const data = await saveHolidaySettings(state, year, holidays);
      applySettings(data);
      dispatch(showSuccessSnackbar(
        holidays.length > 0
          ? `Saved ${holidays.length} public holidays for ${state} ${year}`
          : `Removed maintained public holidays for ${state} ${year}`
      ));
    } catch (error) {
      handleError(error, 'Cannot save public holidays');
    } finally {
      setIsLoading(false);
    }
  };

  const lastUpdated = settings && settings.lastUpdated && settings.lastUpdated._seconds
    ? moment.unix(settings.lastUpdated._seconds).format('DD/MM/YYYY HH:mm')
    : null;

  if (!canEdit) {
    return (
      <div className={classes.root}>
        <Typography variant="h4" gutterBottom>Public Holidays</Typography>
        <Typography variant="body2" color="textSecondary">
          Only admins and menu admins can maintain public holidays.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>Public Holidays</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Holidays saved here are what merchants get from &quot;Load Public Holidays&quot;, ahead of the
        government API. SA has no API, so SA holidays must be maintained here. Saving an empty list
        removes the maintained holidays for that year.
      </Typography>

      <div className={classes.filters}>
        <TextField
          select
          label="State"
          size="small"
          className={classes.select}
          value={state}
          onChange={(event) => setState(event.target.value)}
        >
          {STATES.map((code) => (
            <MenuItem key={code} value={code}>{code}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Year"
          size="small"
          className={classes.select}
          value={year}
          onChange={(event) => setYear(event.target.value)}
        >
          {YEARS.map((option) => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </TextField>
        {settings && (
          <Typography variant="body2" color="textSecondary">
            {settings.source ? SOURCE_LABELS[settings.source] || settings.source : 'Nothing saved yet'}
            {lastUpdated ? ` · Updated ${lastUpdated}` : ''}
            {settings.updatedBy ? ` by ${settings.updatedBy}` : ''}
          </Typography>
        )}
      </div>
      {isLoading && <LinearProgress />}

      <TableContainer component={Paper} className={classes.section}>
        <Table size="small" aria-label={`${state} ${year} public holidays`}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <TextField
                    type="date"
                    size="small"
                    value={row.date}
                    onChange={(event) => updateRow(row.key, 'date', event.target.value)}
                    inputProps={{ min: `${year}-01-01`, max: `${year}-12-31` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.name}
                    onChange={(event) => updateRow(row.key, 'name', event.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.description}
                    onChange={(event) => updateRow(row.key, 'description', event.target.value)}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Remove holiday" onClick={() => removeRow(row.key)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No public holidays for {state} {year}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.actions}>
        <Button variant="outlined" disabled={isLoading} onClick={addRow}>
          Add Holiday
        </Button>
        <Button variant="contained" color="primary" disabled={isLoading} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default PublicHolidaySettings;
